import React, { useState, useEffect } from 'react';
import { submitOptIn, deletePoolCandidate } from '../../lib/api';

/**
 * Public candidate opt-in page (no recruiter auth). A candidate submits their
 * OWN public profile with explicit consent. They get a deletion link back.
 * Lawful basis = the candidate's consent.
 */

const S = {
  page: {
    minHeight: '100dvh',
    backgroundColor: 'var(--bg-base)',
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 20px',
  },
  card: {
    width: '100%',
    maxWidth: 520,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 18,
  },
  brand: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 },
  mark: {
    width: 30,
    height: 30,
    borderRadius: 8,
    background:
      'linear-gradient(135deg, var(--accent) 0%, var(--accent) 50%, transparent 50%), oklch(95% 0.005 var(--hue))',
    color: 'oklch(20% 0.012 var(--hue))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'var(--font-mono)',
    fontWeight: 700,
    fontSize: 14,
  },
  h1: { fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.02em' },
  sub: { fontSize: 14, color: 'var(--text-muted)', margin: 0, lineHeight: 1.55 },
  label: { display: 'flex', flexDirection: 'column' as const, gap: 5, fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' },
  input: {
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg-surface)',
    color: 'var(--text-primary)',
    fontSize: 14,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  consentRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    fontSize: 13,
    color: 'var(--text-secondary)',
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '12px 14px',
    lineHeight: 1.5,
  },
  submit: {
    padding: '11px 16px',
    borderRadius: 10,
    border: 'none',
    backgroundColor: 'var(--accent)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  notice: { fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 },
  errorBox: {
    padding: '10px 14px',
    borderRadius: 8,
    backgroundColor: 'var(--danger-muted, #fff0f0)',
    border: '1px solid var(--danger, #e53e3e)',
    color: 'var(--danger, #e53e3e)',
    fontSize: 13,
  },
  successBox: {
    padding: '16px',
    borderRadius: 12,
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 10,
  },
  code: {
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    color: 'var(--text-primary)',
    backgroundColor: 'var(--bg-elevated)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    padding: '8px 10px',
    wordBreak: 'break-all' as const,
  },
};

export function OptinPage() {
  const [name, setName] = useState('');
  const [profileUrl, setProfileUrl] = useState('');
  const [headline, setHeadline] = useState('');
  const [location, setLocation] = useState('');
  const [skills, setSkills] = useState('');
  const [summary, setSummary] = useState('');
  const [email, setEmail] = useState('');
  const [available, setAvailable] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ id: string; deleteToken: string } | null>(null);
  const [deleted, setDeleted] = useState<'idle' | 'working' | 'done' | 'error'>('idle');

  // Self-service deletion: /optin?delete=<id>&token=<token>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('delete');
    const token = params.get('token');
    if (!id || !token) return;
    setDeleted('working');
    void deletePoolCandidate(id, token)
      .then(() => setDeleted('done'))
      .catch(() => setDeleted('error'));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const skillList = skills.split(',').map((s) => s.trim()).filter(Boolean);
    if (skillList.length === 0) { setError('Please add at least one skill.'); return; }
    if (!consent) { setError('Consent is required to join the pool.'); return; }

    setLoading(true);
    try {
      const result = await submitOptIn({
        name,
        skills: skillList,
        consent: true,
        ...(profileUrl ? { profileUrl } : {}),
        ...(headline ? { headline } : {}),
        ...(location ? { location } : {}),
        ...(summary ? { summary } : {}),
        ...(email ? { email } : {}),
        ...(available ? { available } : {}),
      });
      setDone(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const deleteUrl = done
    ? `${window.location.origin}/optin?delete=${done.id}&token=${done.deleteToken}`
    : '';

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.brand}>
          <div style={S.mark}>S</div>
          <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)' }}>Sourcr · Talent Pool</span>
        </div>

        {deleted !== 'idle' ? (
          <div style={S.successBox}>
            <h1 style={S.h1}>
              {deleted === 'done' ? 'Your data was deleted' : deleted === 'error' ? 'Deletion failed' : 'Deleting…'}
            </h1>
            <p style={S.sub}>
              {deleted === 'done'
                ? 'All information you submitted has been removed from Sourcr.'
                : deleted === 'error'
                  ? 'That deletion link is invalid or the record no longer exists.'
                  : 'Removing your data…'}
            </p>
          </div>
        ) : done ? (
          <div style={S.successBox}>
            <h1 style={S.h1}>You're in the pool 🎉</h1>
            <p style={S.sub}>
              Thanks for opting in. Recruiters searching for your skills may now find your public profile.
            </p>
            <p style={{ ...S.notice, margin: 0 }}>
              Keep this private deletion link — opening it removes all your data from Sourcr:
            </p>
            <div style={S.code}>{deleteUrl}</div>
          </div>
        ) : (
          <>
            <h1 style={S.h1}>Join the talent pool</h1>
            <p style={S.sub}>
              Submit your own public profile so recruiters can find you. You stay in control — you can delete your
              data at any time with the link we give you.
            </p>

            {error !== null && <div style={S.errorBox}>{error}</div>}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <label style={S.label}>Full name *
                <input style={S.input} value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
              </label>
              <label style={S.label}>Skills (comma-separated) *
                <input style={S.input} value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="React, TypeScript, Node.js" required />
              </label>
              <label style={S.label}>Public profile URL
                <input style={S.input} type="url" value={profileUrl} onChange={(e) => setProfileUrl(e.target.value)} placeholder="https://github.com/you" />
              </label>
              <label style={S.label}>Headline
                <input style={S.input} value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Senior Frontend Engineer" />
              </label>
              <label style={S.label}>Location
                <input style={S.input} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Kyiv, Ukraine" />
              </label>
              <label style={S.label}>Email (optional, for recruiters)
                <input style={S.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              <label style={S.label}>Availability
                <input style={S.input} value={available} onChange={(e) => setAvailable(e.target.value)} placeholder="Immediately / 1 month" />
              </label>
              <label style={S.label}>Short summary
                <textarea style={{ ...S.input, minHeight: 70, resize: 'vertical' }} value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={2000} />
              </label>

              <label style={S.consentRow}>
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  I consent to Sourcr storing the information above so recruiters can contact me. I understand this is
                  my own data, that I can request deletion at any time, and that Sourcr will not share it beyond its
                  recruiter platform.
                </span>
              </label>

              <button type="submit" style={S.submit} disabled={loading}>
                {loading ? 'Submitting…' : 'Join the pool'}
              </button>

              <p style={S.notice}>
                Sourcr only stores what you submit here. We never log into other platforms on your behalf. Retention:
                until you delete it. You'll get a private deletion link after submitting.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
